// 🧱🗂️ The room frame's PANEL registry + this device's layout
// (SPACES_REDESIGN_BRAINSTORM_2026_09_29 §5.2 · §5.3 · §5.6-§5.10; mockup
// rooms-by-purpose.html frame A: rails that stack panels, each rail ending in
// a quiet "+ Add a tool").
//
// ONLY panels that really exist (no fake tools: awareness without ability is
// the trap §5.6 names):
//   files   the manila folder (FilesFolder, a drawer that portals itself):
//           a DOOR in a rail, the Files button on phones
//   notes   the auto notes (NotesPanel): INLINE in a rail, its own sheet on phones
//   people  who's here, from the page's users (utils/playerKind: an AI wears
//           the cyan ring, never read from is_human): INLINE, drawn by the kit
//   export  the Export this chat dialog (ExportChatDialog, already in the
//           tree): a DOOR
// `render` names what draws the body; the frame (components/RoomShell) owns
// the chrome around it and asks the page for the rest (its mount contract).
// A panel whose piece the page did not hand over (no onOpenFiles, no
// renderNotes…) is simply absent: from the rails, the shelf and the phone bar.
//
// `who` is who can USE it (the shelf's badge: 'ai' = "People + AIs", after
// the toolkit mockup's tool list), `cost` is 'free' for every v1 panel.
//
// THE LAYOUT (Nina's call 18 default, §9: owner default, viewer override):
// the owner's default IS the room's preset (utils/roomPurposeLayout; there is
// no owner edit route yet), and each device keeps its own changes in
// localStorage: which panels sit on which rail, and whether each is folded.
// Guarded: a private window or blocked storage falls back to the preset,
// and a read never throws. A stored layout for another purpose is ignored.
//
// Pure: no React, no DOM (storage is passed in, or read from window, guarded).

export const PANEL_SIDES = Object.freeze(['left', 'right']);
export const WHO_LABEL = Object.freeze({ ai: 'People + AIs', people: 'People' });
export const COST_LABEL = Object.freeze({ free: 'Free' });

const ALL = ['learn', 'research'];

export const PANELS = Object.freeze([
  Object.freeze({
    id: 'files', label: 'Files', icon: 'folder', defaultSide: 'left', purposes: Object.freeze(ALL), render: 'files',
    mode: 'door', phoneBar: true, who: 'ai', cost: 'free',
    blurb: 'Every doc, image and drawing shared here.', door: 'Open the room folder',
  }),
  Object.freeze({
    id: 'notes', label: 'Notes', icon: 'notes', defaultSide: 'right', purposes: Object.freeze(ALL), render: 'notes',
    mode: 'inline', phoneBar: false, who: 'people', cost: 'free',
    blurb: 'Decisions, to-dos and questions, kept.',
  }),
  Object.freeze({
    id: 'people', label: 'People', icon: 'people', defaultSide: 'right', purposes: Object.freeze(ALL), render: 'people',
    mode: 'inline', phoneBar: true, who: 'people', cost: 'free',
    blurb: 'Who’s here, people and AIs.',
  }),
  Object.freeze({
    id: 'export', label: 'Export', icon: 'download', defaultSide: 'left', purposes: Object.freeze(ALL), render: 'export',
    mode: 'door', phoneBar: false, who: 'people', cost: 'free',
    blurb: 'Save this chat as a file.', door: 'Export this chat',
  }),
]);

export const PANEL_IDS = Object.freeze(PANELS.map((p) => p.id));

/** The registry row for an id, or null. */
export function panelFor(id) {
  return PANELS.find((p) => p.id === id) || null;
}

/** The registry rows a purpose may use, in registry order. */
export function panelsFor(purpose) {
  return PANELS.filter((p) => p.purposes.includes(purpose));
}

/**
 * Which panels the page can actually draw, from what it handed the frame:
 *   { files: !!onOpenFiles, notes: !!renderNotes, people: Array.isArray(users), export: !!onOpenExport }
 * → a Set of ids.
 */
export function availableIds(have = {}) {
  return new Set(PANEL_IDS.filter((id) => !!have[id]));
}

// ── the layout ──────────────────────────────────────────────────────────────
// { purpose, left: [{ id, collapsed }], right: [{ id, collapsed }] }

/** The owner's default: the preset's rails, nothing folded. */
export function defaultLayout(preset) {
  const purpose = (preset && preset.purpose) || null;
  const side = (s) => ((preset && preset.panels && Array.isArray(preset.panels[s])) ? preset.panels[s] : []);
  return normalizeLayout({ purpose, left: side('left').map((id) => ({ id })), right: side('right').map((id) => ({ id })) }, purpose);
}

/**
 * Any stored shape → a clean layout for `purpose`: unknown ids, ids this
 * purpose may not use and repeats (the first wins, across both rails) are
 * dropped; `collapsed` is a real boolean. Bare ids are accepted.
 */
export function normalizeLayout(raw, purpose) {
  const seen = new Set();
  const side = (list) => (Array.isArray(list) ? list : []).map((e) => {
    const id = typeof e === 'string' ? e : (e && typeof e.id === 'string' ? e.id : null);
    const p = id ? panelFor(id) : null;
    if (!p || !p.purposes.includes(purpose) || seen.has(id)) return null;
    seen.add(id);
    return { id, collapsed: !!(e && typeof e === 'object' && e.collapsed === true) };
  }).filter(Boolean);
  const r = raw && typeof raw === 'object' ? raw : {};
  return { purpose: purpose || null, left: side(r.left), right: side(r.right) };
}

/** Every placed id, left rail first. */
export function placedIds(layout) {
  return [...(layout?.left || []), ...(layout?.right || [])].map((e) => e.id);
}

/** Which rail holds a panel ('left' | 'right'), or null. */
export function sideOf(layout, id) {
  if ((layout?.left || []).some((e) => e.id === id)) return 'left';
  if ((layout?.right || []).some((e) => e.id === id)) return 'right';
  return null;
}

/** Registry panels for this purpose that the page can draw and no rail holds yet. */
export function unplacedPanels(layout, purpose, available = null) {
  const placed = new Set(placedIds(layout));
  return panelsFor(purpose).filter((p) => !placed.has(p.id) && (!available || available.has(p.id)));
}

const without = (list, id) => (list || []).filter((e) => e.id !== id);
const sideKey = (s) => (s === 'left' ? 'left' : 'right');

/** Put a panel at the END of a rail (from wherever it was). */
export function addPanel(layout, id, side) {
  const p = panelFor(id);
  if (!p || !p.purposes.includes(layout?.purpose)) return layout;
  const s = sideKey(side || p.defaultSide);
  const next = { purpose: layout.purpose, left: without(layout.left, id), right: without(layout.right, id) };
  next[s] = [...next[s], { id, collapsed: false }];
  return next;
}

/** Take a panel off its rail (it goes back to the shelf). */
export function removePanel(layout, id) {
  if (!sideOf(layout, id)) return layout;
  return { purpose: layout.purpose, left: without(layout.left, id), right: without(layout.right, id) };
}

/** Move a panel to the other rail, keeping its fold. */
export function movePanel(layout, id) {
  const from = sideOf(layout, id);
  if (!from) return layout;
  const to = from === 'left' ? 'right' : 'left';
  const entry = layout[from].find((e) => e.id === id);
  const next = { purpose: layout.purpose, left: without(layout.left, id), right: without(layout.right, id) };
  next[to] = [...next[to], { ...entry }];
  return next;
}

/** Fold or unfold a panel. */
export function toggleCollapsed(layout, id) {
  const from = sideOf(layout, id);
  if (!from) return layout;
  return { ...layout, [from]: layout[from].map((e) => (e.id === id ? { ...e, collapsed: !e.collapsed } : e)) };
}

/** Same rails, same order, same folds. */
export function sameLayout(a, b) {
  const flat = (l) => JSON.stringify([(l?.left || []).map((e) => [e.id, !!e.collapsed]), (l?.right || []).map((e) => [e.id, !!e.collapsed])]);
  return flat(a) === flat(b);
}

// ── this device's copy ──────────────────────────────────────────────────────
export const LAYOUT_KEY_PREFIX = 'room_shell_layout_v1:';
export const layoutKey = (gameId) => `${LAYOUT_KEY_PREFIX}${gameId}`;

const storeOf = (storage) => {
  if (storage !== undefined) return storage;
  try { return typeof window !== 'undefined' ? window.localStorage : null; } catch (e) { return null; }
};

/** This device's layout for the room, else the preset's (never throws). */
export function loadLayout(gameId, preset, storage) {
  const fallback = defaultLayout(preset);
  if (!gameId || !preset) return fallback;
  try {
    const ls = storeOf(storage);
    const raw = ls && ls.getItem(layoutKey(gameId));
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.v !== 1 || parsed.purpose !== preset.purpose) return fallback;
    return normalizeLayout(parsed, preset.purpose);
  } catch (e) {
    return fallback;
  }
}

/**
 * Keep this device's layout. The preset's own layout is stored as NOTHING
 * (the key is removed), so a room whose preset changes later follows it.
 * → true when storage took it.
 */
export function saveLayout(gameId, layout, preset, storage) {
  if (!gameId || !layout) return false;
  try {
    const ls = storeOf(storage);
    if (!ls) return false;
    if (preset && sameLayout(layout, defaultLayout(preset))) {
      ls.removeItem(layoutKey(gameId));
      return true;
    }
    ls.setItem(layoutKey(gameId), JSON.stringify({ v: 1, purpose: layout.purpose, left: layout.left, right: layout.right }));
    return true;
  } catch (e) {
    return false;
  }
}
